import * as v from 'valibot';

/** Contentful asset id for a wizard selectable illustration. */
const assetIdSchema = v.pipe(
  v.string(),
  v.regex(
    /^[a-zA-Z0-9]+$/,
    'Lighting Advisor images must be Contentful asset ids.'
  )
);

const optionImageSchema = v.object({
  back_light: v.optional(assetIdSchema),
  bar_light: v.optional(assetIdSchema),
  coaxial_light: v.optional(assetIdSchema),
  dark_field_light: v.optional(assetIdSchema),
  dome_light: v.optional(assetIdSchema),
  flat_dome_light: v.optional(assetIdSchema),
  ring_light: v.optional(assetIdSchema),
  spot_light: v.optional(assetIdSchema),
  factory_automation: v.optional(assetIdSchema),
  medical: v.optional(assetIdSchema),
  retail: v.optional(assetIdSchema),
  traffic: v.optional(assetIdSchema),
  logistics: v.optional(assetIdSchema),
  agriculture: v.optional(assetIdSchema),
  sports: v.optional(assetIdSchema)
});

const step1ExampleObjectSchema = v.object({
  normal: v.optional(assetIdSchema),
  back_light: v.optional(assetIdSchema),
  bar_light: v.optional(assetIdSchema),
  coaxial_light: v.optional(assetIdSchema),
  dark_field_light: v.optional(assetIdSchema),
  dome_light: v.optional(assetIdSchema),
  flat_dome_light: v.optional(assetIdSchema),
  ring_light: v.optional(assetIdSchema),
  spot_light: v.optional(assetIdSchema)
});

/**
 * Step 2 setup sketches, keyed by `<object moving>_<ambient light>` plus an
 * optional `_distance` / `_length` / `_width` suffix for the focused input.
 */
const step2ExampleSchema = v.record(v.string(), assetIdSchema);

const exampleImageSchema = v.object({
  step1: v.optional(
    v.object({
      object1: v.optional(step1ExampleObjectSchema),
      object2: v.optional(step1ExampleObjectSchema),
      object3: v.optional(step1ExampleObjectSchema)
    })
  ),
  step2: v.optional(step2ExampleSchema),
  step3: v.optional(
    v.object({
      normal: v.optional(assetIdSchema),
      normal_infrared: v.optional(assetIdSchema),
      normal_swir: v.optional(assetIdSchema),
      white: v.optional(assetIdSchema),
      blue_green_features: v.optional(assetIdSchema),
      red_yellow_features: v.optional(assetIdSchema),
      infrared: v.optional(assetIdSchema),
      swir: v.optional(assetIdSchema)
    })
  )
});

/** Question info illustrations shown next to the tooltip text, keyed by question id. */
const overlayImageSchema = v.record(v.string(), assetIdSchema);

const lightingAdvisorModeSchema = v.object({
  mode: v.optional(v.picklist(['tiles', 'wizard']))
});

const accessoryConfigurationSchema = v.object({
  slpControllerSku: v.optional(v.string()),
  twoChannelControllerSku: v.optional(v.string()),
  fourChannelControllerSku: v.optional(v.string()),
  controllerConnectCableSku: v.optional(v.string()),
  powerCordSkus: v.optional(v.array(v.string())),
  blacklist: v.optional(v.array(v.string()))
});

const lightingAdvisorImagesSchema = v.object({
  options: v.optional(optionImageSchema),
  overlays: v.optional(overlayImageSchema),
  examples: v.optional(exampleImageSchema)
});

export const lightingAdvisorConfigurationSchema = v.object({
  mode: v.optional(v.picklist(['tiles', 'wizard'])),
  target: v.optional(v.string()),
  accessories: v.optional(accessoryConfigurationSchema),
  images: v.optional(lightingAdvisorImagesSchema)
});

export type LightingAdvisorConfiguration = v.InferOutput<
  typeof lightingAdvisorConfigurationSchema
>;

export type LightingAdvisorAccessoryConfiguration = v.InferOutput<
  typeof accessoryConfigurationSchema
>;

export type LightingAdvisorMode = 'tiles' | 'wizard';

function parseLightingAdvisorImages(
  raw: unknown
): LightingAdvisorConfiguration['images'] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const input = raw as Record<string, unknown>;
  const options = v.safeParse(optionImageSchema, input.options ?? {});
  const overlays = v.safeParse(overlayImageSchema, input.overlays ?? {});
  const examples = v.safeParse(exampleImageSchema, input.examples ?? {});

  return {
    ...(options.success && input.options ? { options: options.output } : {}),
    ...(overlays.success && input.overlays
      ? { overlays: overlays.output }
      : {}),
    ...(examples.success && input.examples ? { examples: examples.output } : {})
  };
}

export function parseLightingAdvisorConfiguration(
  raw: unknown
): LightingAdvisorConfiguration {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const input = raw as Record<string, unknown>;
  const base = v.safeParse(
    v.object({
      mode: v.optional(v.picklist(['tiles', 'wizard'])),
      target: v.optional(v.string())
    }),
    input
  );
  const accessories = v.safeParse(
    accessoryConfigurationSchema,
    input.accessories ?? {}
  );
  const images = parseLightingAdvisorImages(input.images);

  return {
    ...(base.success ? base.output : {}),
    ...(accessories.success && input.accessories
      ? { accessories: accessories.output }
      : {}),
    ...(images ? { images } : {})
  };
}

export function parseLightingAdvisorAccessoryConfiguration(
  raw: unknown
): LightingAdvisorAccessoryConfiguration {
  const result = v.safeParse(accessoryConfigurationSchema, raw ?? {});
  return result.success ? result.output : {};
}

export function parseLightingAdvisorMode(raw: unknown): LightingAdvisorMode {
  const result = v.safeParse(lightingAdvisorModeSchema, raw ?? {});
  if (result.success && result.output.mode === 'wizard') {
    return 'wizard';
  }
  return 'tiles';
}
